import { Check, Plus, Sparkles, RefreshCw, AlertTriangle, Info } from "lucide-react";

const f = (lang) => (lang === "ar" ? "font-arabic" : "font-outfit");

// Per-service scope questions. They feed the proposal's deliverables only — they do NOT change the price.
export const SERVICE_SCOPE = {
  graphic_design: [
    { key: "designs", ar: "عدد التصاميم", en: "Number of designs", ph: "٣" },
    { key: "formats", ar: "صيغ التسليم", en: "Delivery formats", ph: "PNG, SVG, PDF" },
  ],
  web_dev: [
    { key: "pages", ar: "عدد الصفحات", en: "Number of pages", ph: "٥" },
    { key: "features", ar: "الوظائف المطلوبة", en: "Required features", ph: "نموذج تواصل، لوحة تحكم" },
  ],
  app_dev: [
    { key: "screens", ar: "عدد الشاشات", en: "Number of screens", ph: "١٢" },
    { key: "platforms", ar: "المنصات", en: "Platforms", ph: "iOS + Android" },
  ],
  content_writing: [
    { key: "pieces", ar: "عدد القطع", en: "Number of pieces", ph: "٤ مقالات" },
    { key: "words", ar: "عدد الكلمات", en: "Word count", ph: "١٠٠٠ كلمة لكل مقال" },
  ],
  digital_marketing: [
    { key: "channels", ar: "القنوات", en: "Channels", ph: "إنستغرام، سناب" },
    { key: "posts", ar: "عدد المنشورات / الحملات", en: "Posts / campaigns", ph: "١٢ منشور شهريًا" },
  ],
  ui_ux_design: [
    { key: "screens", ar: "عدد الشاشات", en: "Number of screens", ph: "١٥" },
    { key: "deliverables", ar: "التسليمات", en: "Deliverables", ph: "Figma + نموذج تفاعلي" },
  ],
  video_editing: [
    { key: "duration", ar: "مدة الفيديو النهائي", en: "Final video length", ph: "٦٠ ثانية" },
    { key: "raw", ar: "حجم المادة الخام", en: "Raw footage size", ph: "٢٠ دقيقة" },
  ],
  seo_consulting: [
    { key: "pages", ar: "عدد الصفحات المستهدفة", en: "Target pages", ph: "١٠" },
    { key: "keywords", ar: "عدد الكلمات المفتاحية", en: "Keywords", ph: "٢٠" },
  ],
};

export const scopeToLines = (service, details, lang) =>
  (SERVICE_SCOPE[service] || [])
    .filter((q) => (details?.[q.key] || "").trim())
    .map((q) => `${lang === "ar" ? q.ar : q.en}: ${details[q.key].trim()}`);

export function CalcStepper({ lang, dark, started, done }) {
  const ar = lang === "ar";
  const steps = [
    { n: 1, label: ar ? "الخدمة" : "Service", ok: true },
    { n: 2, label: ar ? "تفاصيل المشروع" : "Project details", ok: started || done },
    { n: 3, label: ar ? "النتيجة" : "Result", ok: done },
  ];
  return (
    <ol data-testid="calc-stepper" className={`flex items-center gap-1.5 text-[11px] font-bold ${f(lang)}`}>
      {steps.map((s, i) => (
        <li key={s.n} data-testid={`calc-step-${s.n}`} className="flex items-center gap-1.5 flex-1">
          <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 font-mono-num transition-colors ${
            s.ok ? "bg-[#18c98f] border-[#18c98f] text-[#182138]" : dark ? "border-[#2e3a5c] text-white/60" : "border-[#d4d4dc] text-[#2d2c35]/60"}`}>
            {s.ok && s.n < 3 ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : s.n}
          </span>
          <span className={s.ok ? (dark ? "text-white" : "text-[#182138]") : dark ? "text-white/55" : "text-[#2d2c35]/55"}>{s.label}</span>
          {i < steps.length - 1 && <span className={`h-px flex-1 ${s.ok ? "bg-[#18c98f]/60" : dark ? "bg-[#2e3a5c]" : "bg-[#e5e5ea]"}`} />}
        </li>
      ))}
    </ol>
  );
}

export function OtherServiceField({ lang, dark, open, setOpen, value, setValue, categoryLabel }) {
  const ar = lang === "ar";
  if (!open) {
    return (
      <button data-testid="service-other-toggle" type="button" onClick={() => setOpen(true)}
        className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-[#18c98f] hover:underline ${f(lang)}`}>
        <Plus className="h-3 w-3" /> {ar ? "خدمة أخرى؟ اكتب اسمها" : "Other service? Type its name"}
      </button>
    );
  }
  return (
    <div className="mt-2 space-y-1">
      <input data-testid="service-other-input" value={value} onChange={(e) => setValue(e.target.value.slice(0, 60))}
        placeholder={ar ? "مثال: تصميم معماري، تعليق صوتي..." : "e.g. Architectural design, voice-over..."}
        className={`w-full h-8 rounded-md border px-2 text-[12px] ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/35" : "bg-white border-[#e5e5ea]"} ${f(lang)}`} />
      <p className={`text-[10.5px] leading-snug ${dark ? "text-white/60" : "text-[#2d2c35]/65"} ${f(lang)}`}>
        {ar
          ? `يظهر هذا الاسم في النتيجة وعرض السعر. يُحسب السعر على أقرب تصنيف تختاره أعلاه (الحالي: ${categoryLabel}).`
          : `This name appears in the result and proposal. Pricing uses the closest category you pick above (now: ${categoryLabel}).`}
        <button type="button" data-testid="service-other-clear" onClick={() => { setValue(""); setOpen(false); }} className="ms-1 underline">
          {ar ? "إلغاء" : "Cancel"}
        </button>
      </p>
    </div>
  );
}

export function UnitHint({ lang, unit, dark }) {
  const ar = lang === "ar";
  const txt = {
    hour: ar ? "السعر = سعر الساعة × عدد الساعات" : "Price = hourly rate × hours",
    day: ar ? "اليوم = ٨ ساعات عمل" : "1 day = 8 working hours",
    project: ar ? "المشروع الكامل = تقدير نحو ٢٠ ساعة عمل لكل مشروع" : "Full project ≈ 20 working hours each",
  }[unit];
  return (
    <p data-testid="unit-hint" className={`text-[10.5px] flex items-center gap-1 ${dark ? "text-white/60" : "text-[#2d2c35]/65"} ${f(lang)}`}>
      <Info className="h-3 w-3 shrink-0" /> {txt}
    </p>
  );
}

export function ScopeDetails({ lang, dark, service, value, onChange }) {
  const ar = lang === "ar";
  const qs = SERVICE_SCOPE[service] || [];
  if (!qs.length) return null;
  return (
    <details data-testid="scope-details" className={`rounded-lg border px-2 py-1.5 ${dark ? "border-[#2e3a5c]" : "border-[#e5e5ea]"}`}>
      <summary className={`cursor-pointer text-[11px] font-bold ${dark ? "text-white/80" : "text-[#2d2c35]/80"} ${f(lang)}`}>
        {ar ? "تفاصيل لعرض السعر (اختياري — لا تغيّر السعر)" : "Details for the proposal (optional — doesn't change the price)"}
      </summary>
      <div className="grid grid-cols-2 gap-1.5 mt-1.5">
        {qs.map((q) => (
          <label key={q.key} className="block">
            <span className={`block text-[9.5px] mb-0.5 ${dark ? "text-white/60" : "text-[#2d2c35]/70"} ${f(lang)}`}>{ar ? q.ar : q.en}</span>
            <input data-testid={`scope-${q.key}`} value={value?.[q.key] || ""} placeholder={q.ph}
              onChange={(e) => onChange({ ...value, [q.key]: e.target.value.slice(0, 80) })}
              className={`w-full h-8 rounded-md border px-2 text-[11px] ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/30" : "bg-white border-[#e5e5ea]"} ${f(lang)}`} />
          </label>
        ))}
      </div>
    </details>
  );
}

const quotaLine = (q, ar) => {
  if (!q) return ar ? "جارٍ التحقق من رصيدك..." : "Checking your balance...";
  switch (q.next_cost) {
    case "subscription": return ar ? "مشمول في اشتراكك — لن يُخصم شيء." : "Included in your subscription.";
    case "credit": return ar ? `سيستهلك هذا الطلب ١ من رصيدك المدفوع (المتبقي ${q.credits.toLocaleString("ar-SA")}).` : `Uses 1 paid credit (${q.credits} left).`;
    case "free": return ar ? `هذا الطلب مجاني — متبقٍ لك ${q.free_remaining.toLocaleString("ar-SA")} من ${q.free_limit.toLocaleString("ar-SA")} اقتراحات مجانية هذا الشهر، بدون تسجيل.` : `This request is free — ${q.free_remaining} of ${q.free_limit} free suggestions left this month, no sign-up.`;
    default: return ar ? "انتهت اقتراحاتك المجانية لهذا الشهر — اشترِ رصيدًا للمتابعة." : "Free suggestions used up this month — buy credits to continue.";
  }
};

export function AiPanel({ lang, dark, quota, loading, error, onRequest, disabled, onBuy }) {
  const ar = lang === "ar";
  return (
    <div data-testid="ai-panel" className={`space-y-2 ${f(lang)}`}>
      <button data-testid="pricing-calc-bottom" type="button" onClick={onRequest} disabled={disabled || loading}
        className={`w-full h-12 rounded-[14px] inline-flex items-center justify-center gap-2 font-black text-[15px] text-white bg-[#18c98f] hover:brightness-110 active:translate-y-px transition-[filter,transform] disabled:opacity-55 shadow-[0_4px_0_0_#0e8a62,0_12px_28px_-10px_rgba(24,201,143,0.6)]`}>
        {loading ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
        {loading
          ? (ar ? "جارٍ حساب السعر..." : "Calculating...")
          : error ? (ar ? "إعادة المحاولة" : "Try again") : (ar ? "٣. احسب الآن" : "3. Calculate now")}
      </button>
      {error && (
        <div data-testid="ai-error" role="alert" className="rounded-lg border border-[#f97316]/50 bg-[#f97316]/10 p-2 text-[11px] text-[#f97316] flex items-start gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span className="flex-1">{error}</span>
        </div>
      )}
      <p data-testid="ai-quota-line" className={`text-center text-[11px] font-bold ${quota?.next_cost === "blocked" ? "text-[#f97316]" : dark ? "text-white/65" : "text-[#2d2c35]/70"}`}>
        {quotaLine(quota, ar)}
        {quota?.next_cost === "blocked" && (
          <button data-testid="ai-quota-buy" type="button" onClick={onBuy} className="ms-1 underline text-[#18c98f]">{ar ? "شراء رصيد" : "Buy credits"}</button>
        )}
      </p>
    </div>
  );
}
